import { Button } from "@rumbledirect/ui";
import { ServicePartner } from "@rumble-it/common-lib/types/models/ServicePartner.interface";
import {
  Card,
  Col,
  Form,
  FormInstance,
  Input,
  InputNumber,
  Row,
  Space,
} from "antd";

type AddressFormValues = {
  street?: string | null;
  streetNumber?: string | null;
  streetNumberAddition?: string | null;
  postalCode?: string | null;
  city?: string | null;
  country?: string | null;
};

export type RootServicepartnerFormValues = {
  name?: string;
  groupName?: string | null;
  email?: string | null;
  telephone?: string | null;
  website?: string | null;
  dwproAudaboxNumber?: number | null;
  externalReference?: string | null;
  address?: AddressFormValues;
  postalAddress?: AddressFormValues;
};

const isCompleteAddress = (
  address: AddressFormValues | undefined,
): address is Required<
  Pick<
    AddressFormValues,
    "street" | "streetNumber" | "postalCode" | "city" | "country"
  >
> &
  AddressFormValues =>
  !!address &&
  !!address.street &&
  !!address.streetNumber &&
  !!address.postalCode &&
  !!address.city &&
  !!address.country;

export const buildAddressPayload = (address: AddressFormValues | undefined) => {
  if (!isCompleteAddress(address)) {
    return undefined;
  }

  return {
    street: address.street as string,
    streetNumber: address.streetNumber as string,
    streetNumberAddition: address.streetNumberAddition ?? null,
    postalCode: address.postalCode as string,
    city: address.city as string,
    country: address.country as string,
  };
};

export const addressInitialValues = (
  servicePartner: ServicePartner | undefined,
  field: "address" | "postalAddress",
): AddressFormValues | undefined => {
  const address = servicePartner?.[field];

  if (!address) return undefined;

  return {
    street: address.street ?? null,
    streetNumber: address.streetNumber ?? null,
    streetNumberAddition: address.streetNumberAddition ?? null,
    postalCode: address.postalCode ?? null,
    city: address.city ?? null,
    country: address.country ?? null,
  };
};

type RootServicepartnerFormProps = {
  form: FormInstance<RootServicepartnerFormValues>;
  title: string;
  loading?: boolean;
  submitting?: boolean;
  submitLabel: string;
  onSubmit: (values: RootServicepartnerFormValues) => void;
  onCancel: () => void;
};

export const RootServicepartnerForm = ({
  form,
  title,
  loading = false,
  submitting = false,
  submitLabel,
  onSubmit,
  onCancel,
}: RootServicepartnerFormProps) => (
  <Card title={title} loading={loading}>
    <Form form={form} layout="vertical" onFinish={onSubmit}>
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            label="Naam"
            name="name"
            rules={[{ required: true, message: "Verplicht veld" }]}
          >
            <Input />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item label="Groep" name="groupName">
            <Input />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item label="E-mail" name="email">
            <Input type="email" />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item label="Telefoon" name="telephone">
            <Input />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item label="Website" name="website">
            <Input />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item label="Externe referentie" name="externalReference">
            <Input />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item label="Audabox nummer" name="dwproAudaboxNumber">
            <InputNumber
              min={1}
              step={1}
              precision={0}
              style={{ width: "100%" }}
            />
          </Form.Item>
        </Col>
      </Row>

      <Card type="inner" title="Adres" style={{ marginTop: 16 }}>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item label="Straat" name={["address", "street"]}>
              <Input />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item label="Nummer" name={["address", "streetNumber"]}>
              <Input />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item
              label="Toevoeging"
              name={["address", "streetNumberAddition"]}
            >
              <Input />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label="Postcode" name={["address", "postalCode"]}>
              <Input />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label="Stad" name={["address", "city"]}>
              <Input />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label="Land" name={["address", "country"]}>
              <Input />
            </Form.Item>
          </Col>
        </Row>
      </Card>

      <Card type="inner" title="Postadres" style={{ marginTop: 16 }}>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item label="Straat" name={["postalAddress", "street"]}>
              <Input />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item label="Nummer" name={["postalAddress", "streetNumber"]}>
              <Input />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item
              label="Toevoeging"
              name={["postalAddress", "streetNumberAddition"]}
            >
              <Input />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label="Postcode" name={["postalAddress", "postalCode"]}>
              <Input />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label="Stad" name={["postalAddress", "city"]}>
              <Input />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label="Land" name={["postalAddress", "country"]}>
              <Input />
            </Form.Item>
          </Col>
        </Row>
      </Card>

      <Form.Item label={null} style={{ marginTop: 16, marginBottom: 0 }}>
        <Space style={{ justifyContent: "flex-end", width: "100%" }}>
          <Button variant="light" onClick={onCancel}>
            Annuleren
          </Button>
          <Button type="primary" htmlType="submit" loading={submitting}>
            {submitLabel}
          </Button>
        </Space>
      </Form.Item>
    </Form>
  </Card>
);
